<template>
  <main id="content" aria-labelledby="main-title">

    <div class="hero--info bg-faun">
      <div class="pt-16 sm:pt-24 pb-8">
        <Container>
          <FadeIn class="text-center max-w-3xl mx-auto">
            <h1 id="main-title" class="text-section-title">
              Workplace integrity glossary
            </h1>
            <p class="text-subtitle mt-4">
              A-Z glossary of speak-up, integrity, and compliance terms for Australian and New Zealand workplaces.
            </p>
          </FadeIn>
        </Container>
      </div>
    </div>

    <Section margins="reset-top">
      <Container>
        <FadeIn class="max-w-4xl mx-auto">

          <div class="prose max-w-none mb-8">
            <p>
              This glossary defines the terms that come up most often when organisations
              in Australia and New Zealand design and run speak-up and whistleblower
              schemes, investigations, case management, and compliance programmes. Each
              entry explains what the term means, how it applies under Australian and New
              Zealand law, and links to detailed articles where the practical obligations
              and processes live.
            </p>
            <p>
              Terms are grouped by category below. Use this page as a reference whenever
              a piece of legislation, a compliance scheme, or a workplace-integrity
              concept turns up in policy documents, internal training, or audit reports
              and you need a precise definition rather than a marketing description.
            </p>
          </div>

          <section
          v-for="category in categoryOrder"
          :key="category.slug"
          class="glossary-category"
          >
            <template v-if="termsByCategory[category.slug] && termsByCategory[category.slug].length">
              <h2 class="glossary-category__heading">{{ category.label }}</h2>
              <ul class="glossary-category__list not-prose">
                <li
                v-for="term in termsByCategory[category.slug]"
                :key="term.id"
                class="glossary-category__item"
                >
                  <NuxtLink
                  :to="$localePath(`/glossary/${term.slug}`)"
                  class="glossary-category__link"
                  >
                    <span class="glossary-category__term">{{ term.title.split(':')[0].trim() }}</span>
                    <span
                    v-if="term.definition_short"
                    class="glossary-category__description"
                    >
                      {{ term.definition_short }}
                    </span>
                  </NuxtLink>
                </li>
              </ul>
            </template>
          </section>

          <div
          v-if="!terms.length"
          class="prose max-w-none mt-8"
          >
            <p>
              No glossary entries are published in this locale yet. The glossary is
              populated progressively as new terms are drafted, fact-checked, and
              approved for publication. Check back soon for definitions covering
              whistleblower protections, investigations, psychosocial safety, child
              safety schemes, and compliance frameworks relevant to your jurisdiction.
            </p>
          </div>

        </FadeIn>
      </Container>
    </Section>

    <ElementBookDemoCta />

  </main>
</template>

<script setup>
const { getItems } = useCms()
const route = useRoute()

const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

const categoryOrder = [
  { slug: 'workplace-integrity', label: 'Workplace integrity' },
  { slug: 'investigations', label: 'Investigations' },
  { slug: 'compliance', label: 'Compliance' },
  { slug: 'psychosocial-safety', label: 'Psychosocial safety' },
  { slug: 'employment-law', label: 'Employment law' },
  { slug: 'child-safety', label: 'Child safety' },
  { slug: 'human-rights', label: 'Human rights' }
]

const { data } = await useAsyncData(
  currentLocale.value+'-glossary-index',
  () => getItems({
    collection: 'glossary_term',
    params: {
      filter: {
        locale: { _contains: currentLocale.value },
        status: { _in: useRuntimeConfig().public.postStatus }
      },
      fields: ['id', 'slug', 'title', 'category', 'definition_short'],
      sort: 'title',
      limit: -1
    }
  })
)

const terms = computed(() => data.value || [])

const termsByCategory = computed(() => {
  const grouped = {}
  for (const t of terms.value) {
    const cat = t.category || 'workplace-integrity'
    if (!grouped[cat]) grouped[cat] = []
    grouped[cat].push(t)
  }
  return grouped
})

const localePrefix = currentLocale.value === 'nz' ? '/nz' : ''

useHead(
  useSeo(
    {
      meta_title: 'Workplace integrity glossary | Elker',
      meta_description: 'A-Z glossary of speak-up, integrity, and compliance terms for Australian and New Zealand workplaces, with links to detailed articles.',
      meta_image: null
    },
    route.fullPath
  )
)

useSchemaOrg([
  {
    '@type': 'DefinedTermSet',
    name: 'Elker workplace integrity glossary',
    description: 'A-Z glossary of speak-up, integrity, and compliance terms for Australian and New Zealand workplaces.',
    url: `https://elker.com${localePrefix}/glossary`,
    inLanguage: currentLocale.value === 'nz' ? 'en-NZ' : 'en-AU',
    hasDefinedTerm: terms.value.map(t => ({
      '@type': 'DefinedTerm',
      name: t.title,
      description: t.definition_short,
      url: `https://elker.com${localePrefix}/glossary/${t.slug}`
    }))
  },
  {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `https://elker.com${localePrefix}/` },
      { '@type': 'ListItem', position: 2, name: 'Glossary', item: `https://elker.com${localePrefix}/glossary` }
    ]
  }
])
</script>

<style scoped>
.glossary-category {
  margin-bottom: 3rem;
}

.glossary-category__heading {
  font-size: 1.5rem;
  font-weight: 700;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #d7d4cd;
  margin-bottom: 1.5rem;
}

.glossary-category__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
}

.glossary-category__item {
  margin: 0;
}

.glossary-category__link {
  display: block;
  padding: 1rem 1.25rem;
  border: 2px solid #d7d4cd;
  border-radius: 0.5rem;
  text-decoration: none;
  color: inherit;
  height: 100%;
  transition: border-color 0.2s;
}

.glossary-category__link:hover {
  border-color: #5a6ded;
}

.glossary-category__term {
  display: block;
  font-size: 1.125rem;
  font-weight: 600;
}

.glossary-category__description {
  display: block;
  font-size: 0.875rem;
  opacity: 0.75;
  margin-top: 0.4rem;
  line-height: 1.4;
}
</style>
